import "./AuthForms.css";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => searchParams.get('mode') === 'signup' ? 'signup' : 'signin'); // "signin" | "signup" | "reset"
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [branch, setBranch] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const formRef = useRef(null);

  // AutoFill can update the DOM without first delivering a React change event.
  const readCredentials = () => {
    const fields = formRef.current.elements;
    return {
      email: fields.namedItem('email').value,
      password: fields.namedItem('password')?.value || '',
      confirmPassword: fields.namedItem('confirmPassword')?.value || '',
    };
  };
  // Replace the native input on explicit Show/Hide to clear stale AutoFill masking.
  const togglePassword = (confirmation = false) => {
    const current = readCredentials();
    setEmail(current.email);
    setPassword(current.password);
    setConfirmPassword(current.confirmPassword);
    if (confirmation) setShowConfirmPassword(value => !value);
    else setShowPassword(value => !value);
  };

  const handleSubmit = async () => {
    const { email, password, confirmPassword } = readCredentials();
    setEmail(email);
    setPassword(password);
    setConfirmPassword(confirmPassword);
    setLoading(true);
    setError(null);
    setMessage(null);

    if (mode === "reset") {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) setError(resetError.message);
      else setMessage("Password reset email sent. Check your inbox.");
      setLoading(false);
      return;
    }

    if (mode === "signup") {
      if (!firstName.trim() || !lastName.trim()) {
        setError("Please enter your first and last name.");
        setLoading(false);
        return;
      }
      if (!branch) {
        setError("Please select your branch of service.");
        setLoading(false);
        return;
      }
      if (!inviteCode.trim()) {
        setError("An invite code is required during the beta.");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        setLoading(false);
        return;
      }

      // Pre-validate invite code BEFORE creating the auth user.
      // Prevents orphan auth.users rows from bad / already-used codes.
      const { data: checkData, error: checkError } = await supabase.rpc(
        'check_invite_code',
        { p_code: inviteCode.trim() }
      );

      if (checkError || !checkData?.valid) {
        const checkReason = checkData?.reason;
        let checkMsg = "Unable to validate invite code. Please try again.";
        if (checkReason === 'missing_code') checkMsg = "Enter a beta invite code.";
        if (checkReason === 'invalid_code') checkMsg = "That beta invite code was not found.";
        if (checkReason === 'already_used') checkMsg = "That beta invite code has already been used.";
        setError(checkMsg);
        setLoading(false);
        return;
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name:  lastName.trim(),
            branch:     branch,
          },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      // Email-confirmed signup has no session yet. Approval remains server gated.
      // The signed-in activation form retries with the account's own invite code.
      if (!signUpData?.session?.user) {
        setMessage("Check your email to confirm your account. Keep your invite code, then sign in to finish beta access.");
        setLoading(false);
        return;
      }

      // Redeem invite code server-side. SECURITY DEFINER RPC locks the row,
      // rejects unknown/used codes, and marks redemption atomically.
      const newUserId = signUpData.session.user.id;
      if (newUserId) {
        const { data: redeemData, error: redeemError } = await supabase.rpc(
          'redeem_invite_code',
          { p_code: inviteCode.trim(), p_user_id: newUserId }
        );

        if (redeemError || !redeemData?.ok) {
          const reason = redeemData?.reason;
          let msg = "Account was created, but invite redemption failed. Contact support.";
          if (reason === 'missing_code') msg = "Invite code was missing after signup. Contact support.";
          if (reason === 'missing_user') msg = "Account was created, but user ID was missing. Contact support.";
          if (reason === 'invalid_code') msg = "Account was created, but the invite code could not be found. Contact support.";
          if (reason === 'already_used') msg = "Account was created, but that invite code was already used. Contact support.";
          setError(msg);
          setLoading(false);
          return;
        }
      }

      window.dispatchEvent(new Event('vq:refresh-account-type'));
      setMessage("Your beta access is ready.");
      setLoading(false);
      return;
    }

    // Sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) setError(signInError.message);

    setLoading(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setMessage(null);
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setBranch("");
    setInviteCode("");
  };


  return (
    <div className="vq-auth" style={S.root}>
      <div style={S.bg} />
      <form ref={formRef} className="vq-auth-card" style={S.card} noValidate onSubmit={event => { event.preventDefault(); handleSubmit(); }}>

        <div style={S.logo}>Vet<span style={{ color: "#fff" }}>Qualified</span></div>

        <h1 style={S.heading}>
          {mode === "signin" ? "Welcome back" : mode === "signup" ? "Create your account" : "Reset your password"}
        </h1>
        <p style={S.sub}>
          {mode === "signin" && "Sign in to access your veteran career dashboard."}
          {mode === "signup" && "Join the network built for service members."}
          {mode === "reset" && "Enter your email and we'll send you a reset link."}
        </p>

        {mode === "signup" && (
          <>
            <div className="vq-auth-row" style={S.nameRow}>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="member-firstName">First Name</label>
                <input id="member-firstName"
                  style={S.input}
                  type="text"
                  placeholder="First"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}

                />
              </div>
              <div style={{ ...S.field, flex: 1, minWidth: 0 }}>
                <label style={S.label} htmlFor="member-lastName">Last Name</label>
                <input id="member-lastName"
                  style={S.input}
                  type="text"
                  placeholder="Last"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}

                />
              </div>
            </div>

            <div style={S.field}>
              <label style={S.label} htmlFor="member-branch">Branch of Service</label>
              <select id="member-branch"
                style={S.select}
                value={branch}
                onChange={e => setBranch(e.target.value)}
              >
                <option value="">Select your branch…</option>
                <option value="U.S. Army">U.S. Army</option>
                <option value="U.S. Navy">U.S. Navy</option>
                <option value="U.S. Air Force">U.S. Air Force</option>
                <option value="U.S. Marine Corps">U.S. Marine Corps</option>
                <option value="U.S. Coast Guard">U.S. Coast Guard</option>
                <option value="U.S. Space Force">U.S. Space Force</option>
              </select>
            </div>

            <div style={S.field}>
              <label style={S.label} htmlFor="member-inviteCode">Invite Code</label>
              <input id="member-inviteCode"
                style={S.input}
                type="text"
                placeholder="VQ-XXXXX-XXX"
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value)}

                autoComplete="off"
              />
              <div style={S.hint}>VetQualified is currently invite-only during beta.</div>
            </div>
          </>
        )}

        <div style={S.field}>
          <label style={S.label} htmlFor="member-email">Email</label>
          <input id="member-email" name="email"
            style={S.input}
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}

          />
        </div>

        {mode !== "reset" && (
          <div style={S.field}>
            <label style={S.label} htmlFor="member-password">Password</label>
            <div style={S.inputWrap}>
              <input key={showPassword ? "password-visible" : "password-hidden"} id="member-password" name="password" autoCapitalize="none" spellCheck={false}
                style={{ ...S.input, paddingRight: "4.5rem" }}
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}

                autoComplete={mode === "signin" ? "current-password" : "new-password"}
              />
              <button style={S.eyeBtn} onClick={() => togglePassword()} aria-label={showPassword ? "Hide password" : "Show password"} aria-controls="member-password" aria-pressed={showPassword} type="button">
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        )}

        {mode === "signup" && (
          <div style={S.field}>
            <label style={S.label} htmlFor="member-confirmPassword">Confirm Password</label>
            <div style={S.inputWrap}>
              <input key={showConfirmPassword ? "confirmation-visible" : "confirmation-hidden"} id="member-confirmPassword" name="confirmPassword" autoCapitalize="none" spellCheck={false}
                style={{ ...S.input, paddingRight: "4.5rem" }}
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Enter the same password again"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}

                aria-describedby="member-confirm-help"
                autoComplete="new-password"
              />
              <button style={S.eyeBtn} onClick={() => togglePassword(true)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"} aria-controls="member-confirmPassword" aria-pressed={showConfirmPassword} type="button">
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p id="member-confirm-help" style={S.hint}>Type or paste the same password in both fields.</p>
          </div>
        )}

        {error && <div role="alert" style={S.error}>{error}</div>}
        {message && <div role="status" style={S.success}>{message}</div>}

        <button
          style={{ ...S.btn, ...(loading ? S.btnLoading : {}) }}
          type="submit"
          disabled={loading}
        >
          {loading ? "Please wait…" : mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
        </button>

        {mode === "signin" && (
          <>
            <div style={S.forgotWrap}>
              <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("reset")}>
                Forgot your password?
              </button>
            </div>
            <div style={S.toggle}>
              Don't have an account?{" "}
              <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("signup")}>Sign up</button>
            </div>
          </>
        )}

        {mode === "signup" && (
          <div style={S.toggle}>
            Already have an account?{" "}
            <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("signin")}>Sign in</button>
          </div>
        )}

        {mode === "reset" && (
          <div style={S.toggle}>
            <button type="button" className="vq-auth-mode" style={S.link} onClick={() => switchMode("signin")}>← Back to sign in</button>
          </div>
        )}

        <p style={S.toggle}><Link to="/privacy" style={S.link}>Privacy &amp; safety</Link></p>
      </form>
    </div>
  );
}

const S = {
  root: { fontFamily: "'DM Sans', sans-serif", background: "#0B1F3A", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" },
  bg: { position: "fixed", inset: 0, background: "radial-gradient(ellipse 70% 50% at 15% 20%, rgba(201,168,76,0.07) 0%, transparent 60%), repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(201,168,76,0.03) 59px, rgba(201,168,76,0.03) 60px), repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(201,168,76,0.03) 59px, rgba(201,168,76,0.03) 60px)", pointerEvents: "none", zIndex: 0 },
  card: { position: "relative", zIndex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 16, padding: "2.5rem", width: "100%", maxWidth: 420 },
  logo: { fontFamily: "'DM Serif Display', serif", fontSize: "1.5rem", color: "#C9A84C", marginBottom: "1.5rem", letterSpacing: "-0.02em" },
  heading: { fontFamily: "'DM Serif Display', serif", fontSize: "1.75rem", color: "#fff", marginBottom: "0.5rem", letterSpacing: "-0.02em" },
  sub: { fontSize: "0.85rem", color: "#8FA3BE", marginBottom: "1.75rem", lineHeight: 1.6 },
  nameRow: { display: "flex", gap: "0.75rem", marginBottom: "0rem" },
  field: { marginBottom: "1rem" },
  label: { display: "block", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8FA3BE", marginBottom: "0.4rem" },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  input: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "0.7rem 0.9rem", boxSizing: "border-box" },
  eyeBtn: { position: "absolute", right: 2, width: 64, height: 44, justifyContent: "center", background: "transparent", border: "none", color: "#8FA3BE", cursor: "pointer", display: "flex", alignItems: "center", padding: 4, font: "inherit", fontSize: "0.78rem", fontWeight: 700 },
  error: { fontSize: "0.78rem", color: "#f87171", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  success: { fontSize: "0.78rem", color: "#4ade80", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  btn: { width: "100%", padding: "0.8rem", background: "#C9A84C", border: "none", borderRadius: 8, color: "#0B1F3A", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", fontWeight: 800, cursor: "pointer", letterSpacing: "0.04em", marginBottom: "1rem" },
  btnLoading: { background: "#A88830", cursor: "not-allowed" },
  forgotWrap: { textAlign: "right", marginBottom: "0.75rem", marginTop: "-0.25rem" },
  toggle: { fontSize: "0.78rem", color: "#8FA3BE", textAlign: "center" },
  link: { color: "#C9A84C", cursor: "pointer", fontWeight: 600 },
  hint: { fontSize: "0.7rem", color: "#8FA3BE", marginTop: "0.4rem", lineHeight: 1.5 },
  select: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "0.7rem 0.9rem", boxSizing: "border-box", appearance: "none", cursor: "pointer" },
};
